import { useTranslation } from "react-i18next";
import {
  nextVersionName,
  ProjectVersionMenu as DsProjectVersionMenu,
  type ProjectVersion,
  type ProjectVersionLabels
} from "@/zapi-frontend/pages/ProjectsPage/ui/ProjectVersionMenu/blocks/ProjectVersions";
import type { TProject } from "@/zapi-frontend/stores/project";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";
import { getNameInitials } from "@/zapi-frontend/shared/lib/getNameInitials";
import { formatUpdatedAgo } from "../../lib/formatUpdatedAgo";
import { useProjectVersionActions, type TVersionActionsOptions } from "./useProjectVersionActions";

type TProps = TVersionActionsOptions & {
  versionGroupId: string | null | undefined;
  versions: NonNullable<TProject["versions"]>;
  hasEdit: boolean;
  appearance: "glass" | "plain";
  className?: string;
  onSelect: (versionId: string) => void;
};

export function ProjectVersionMenu({
  versionGroupId,
  versions,
  hasEdit,
  appearance,
  className,
  onSelect,
  ...options
}: TProps) {
  const { t } = useTranslation("common");
  const { create, rename, remove } = useProjectVersionActions(options);

  const items: ProjectVersion[] = versions.map((version) => ({
    id: version.id,
    name: displayVersionName(version.name),
    updated: formatUpdatedAgo(version.lastModified || version.date),
    author: version.modifiedBy
      ? { name: version.modifiedBy, initials: getNameInitials(version.modifiedBy) }
      : undefined,
    draft: version.isDraft
  }));
  const defaultName = nextVersionName(versions.map((version) => version.name));

  const labels: ProjectVersionLabels = {
    versions: t("versionMenu.versions"),
    latest: t("versionMenu.latest"),
    draft: t("versionMenu.draft"),
    trigger: (name) => t("versionMenu.trigger", { name }),
    rowActions: (name) => t("versionMenu.rowActions", { name }),
    rename: t("versionMenu.rename"),
    remove: t("versionMenu.remove"),
    removeTitle: t("versions.deleteTitle"),
    removeQuestion: (version) => t("versions.deleteQuestionNoProject", { version }),
    removeBody: t("versions.deleteBody"),
    confirm: t("versions.confirm"),
    keepOneVersion: t("versionMenu.keepOneVersion"),
    newVersion: t("versionMenu.newVersion"),
    createVersion: t("versionMenu.createVersion"),
    creating: t("versionMenu.creating"),
    firstVersionDescription: t("versionMenu.firstVersionDescription"),
    newVersionDescription: t("versionMenu.newVersionDescription"),
    name: t("versionMenu.name"),
    nameTaken: t("versionMenu.nameTaken"),
    basedOn: t("versionMenu.basedOn"),
    draftNoAnalysis: t("versionMenu.draftNoAnalysis"),
    copy: t("versionMenu.copy"),
    frameOnly: t("versionMenu.frameOnly"),
    frameOnlyDescription: t("versionMenu.frameOnlyDescription"),
    fullAnalysis: t("versionMenu.fullAnalysis"),
    fullAnalysisDescription: t("versionMenu.fullAnalysisDescription"),
    noAnalysisYet: t("versionMenu.noAnalysisYet"),
    renameTitle: t("versionMenu.renameTitle"),
    renameDescription: t("versionMenu.renameDescription"),
    save: t("versionMenu.save"),
    saving: t("versionMenu.saving"),
    cancel: t("cancel"),
    close: t("close"),
    loading: t("loading")
  };

  return (
    <DsProjectVersionMenu
      data-prevent-row-nav
      versions={items}
      value={options.currentId}
      onValueChange={(version) => onSelect(version.id)}
      appearance={appearance}
      versioned={Boolean(versionGroupId)}
      editable={hasEdit}
      newVersionName={displayVersionName(defaultName)}
      labels={labels}
      onCreate={(request) =>
        create(
          { ...request, name: request.name === displayVersionName(defaultName) ? defaultName : request.name },
          request.name
        )
      }
      onRename={(version, name) => rename(version.id, name)}
      onRemove={(version) => remove(version.id, version.name, items.find((item) => item.id !== version.id)?.id ?? null)}
      className={className}
    />
  );
}
